/**
 * i18n.js
 *
 * This will setup the i18n language files and locale data for your app.
 *
 */

/**
 * moment
 */
import 'dayjs/locale/de';
import 'dayjs/locale/de-at';
import 'dayjs/locale/en';
import 'dayjs/locale/en-gb';
import 'dayjs/locale/es';
import 'dayjs/locale/fi';
import 'dayjs/locale/fr';
import 'dayjs/locale/it';
import 'dayjs/locale/nl';
import 'dayjs/locale/zh-cn';

// NOTE: Import locale data for antd components
// to keep the localization in synced between App-wide and antd components
import deAntd from 'antd/locale/de_DE';
import enAntd from 'antd/locale/en_GB';
import esAntd from 'antd/locale/es_ES';
import fiAntd from 'antd/locale/fi_FI';
import frAntd from 'antd/locale/fr_FR';
import itAntd from 'antd/locale/it_IT';
import nlAntd from 'antd/locale/nl_BE';

// Polyfills for missing browser API's (IE11 and Safari)
/* eslint-disable global-require */
if (!Intl.PluralRules) {
  require('@formatjs/intl-locale/polyfill');
  require('@formatjs/intl-getcanonicallocales/polyfill');
  require('@formatjs/intl-pluralrules/polyfill');
  require('@formatjs/intl-pluralrules/locale-data/de');
  require('@formatjs/intl-pluralrules/locale-data/en');
  require('@formatjs/intl-pluralrules/locale-data/es');
  require('@formatjs/intl-pluralrules/locale-data/fi');
  require('@formatjs/intl-pluralrules/locale-data/fr');
  require('@formatjs/intl-pluralrules/locale-data/nl');
  require('@formatjs/intl-pluralrules/locale-data/zh');
  require('@formatjs/intl-pluralrules/locale-data/it');
}

if (!Intl.RelativeTimeFormat) {
  require('@formatjs/intl-locale/polyfill');
  require('@formatjs/intl-getcanonicallocales/polyfill');
  require('@formatjs/intl-relativetimeformat/polyfill');
  require('@formatjs/intl-relativetimeformat/locale-data/de');
  require('@formatjs/intl-relativetimeformat/locale-data/en');
  require('@formatjs/intl-relativetimeformat/locale-data/es');
  require('@formatjs/intl-relativetimeformat/locale-data/fi');
  require('@formatjs/intl-relativetimeformat/locale-data/fr');
  require('@formatjs/intl-relativetimeformat/locale-data/nl');
  require('@formatjs/intl-relativetimeformat/locale-data/zh');
  require('@formatjs/intl-relativetimeformat/locale-data/it');
}
/* eslint-enable global-require */

export const DEFAULT_LOCALE = 'en-GB';
export const SUPPORTED_LOCALES = [
  'de-AT',
  'de-DE',
  'en-GB',
  'es-ES',
  'it-IT',
  'fi-FI',
  'fr-FR',
  'nl-BE',
  'zh-CN',
];
// https://github.com/adamwdraper/Numeral-js/tree/master/locales
export const SI_POSTFIXES = ['', 'k', 'M', 'B', 'T'];

// see test
export const flattenMessages = (nestedMessages, prefix = '') =>
  Object.keys(nestedMessages).reduce((messages, key) => {
    const value = nestedMessages[key];
    const prefixedKey = prefix ? `${prefix}.${key}` : key;
    if (typeof value === 'string') {
      Object.assign(messages, { [prefixedKey]: value });
    } else {
      Object.assign(messages, flattenMessages(value, prefixedKey));
    }
    return messages;
  }, {});

export const cacheLocale = locale => localStorage.setItem('locale', locale);

// No locale data for chinese, as this is antd's default in 2.X
export const antdLocaleData = {
  'de-AT': deAntd,
  'de-DE': deAntd,
  'en-GB': enAntd,
  'es-ES': esAntd,
  'fi-FI': fiAntd,
  'fr-FR': frAntd,
  'nl-BE': nlAntd,
  'it-IT': itAntd,
};

export const getLocale = () => {
  // Get the locale from the browser cache, otherwise get the browser's language
  const [navigatorLanguage] = navigator.language || navigator.userLanguage;
  const browserLanguageCode = localStorage.getItem('locale') || navigatorLanguage;
  const locale = SUPPORTED_LOCALES.includes(browserLanguageCode)
    ? browserLanguageCode
    : DEFAULT_LOCALE;
  return locale;
};
